import React, {useEffect, useMemo, useState} from "react";
import {
    Grid, Typography
} from "@mui/material";
import {Add, ChevronRight} from "@mui/icons-material";
import {Cargo, CargoV2, Service} from "~/apps/packages/auto-visits/queries";
import {
    CargoSelectDialog,
    CargoSelectDialogProps
} from "~/apps/packages/auto-visits/components/AutoVisitForm/CargoSelectDialog";
import {useCargoNecessaryServiceListExecutor} from "~/apps/packages/auto-visits/hooks";
import {ServiceSelectDialog} from "~/apps/packages/auto-visits/components/AutoVisitForm/ServiceSelectDialog";
import {CardButton} from "~/apps/core/ui/CardButton";
import {useBooleanHistoryState, useHistoryState} from "~/apps/core/utils";
import {AutoVisitCargoCard} from "~/apps/packages/auto-visits/components/AutoVisitCargoCard";
import {useErrorHandler} from "~/components/ErrorHandling";
import {ConfirmActionDialog} from "~/components/ConfirmActionDialog";


export type CargoWithServices = Cargo & { ServiceList: Service[] };

export type EditableCargoListProps = {
    carrierId: string
    autoVisitId?: string
    value?: CargoWithServices[]
    onChange: (value: CargoWithServices[]) => void
    disabled?: boolean
}


export const EditableCargoList = (props: EditableCargoListProps) => {
    const handleError = useErrorHandler();
    const {carrierId, autoVisitId, value, onChange, disabled: inDisabled} = props;
    const [openSelectDialog, setOpenSelectDialog] = useBooleanHistoryState("selectCargo");
    const getCargoNecessaryServices = useCargoNecessaryServiceListExecutor(carrierId, autoVisitId);
    const [countAppending, setCountAppending] = useState(0);
    const [editCargoID, setEditCargoID, delEditCargoID] = useHistoryState<CargoWithServices["ID"]>({
        name: "cargoID"
    });

    const [cargoToConfirmRemove, setCargoToConfirmRemove] = useState<string>(null);

    const disabled = countAppending > 0 || inDisabled;
    const editCargo: CargoWithServices = useMemo(() => {
        return value?.find(c => c.ID == editCargoID);
    }, [editCargoID, value]);

    const changeCargoList = (cargos: Cargo[]) => {
        const currentCargoByID = {};
        for (const cargo of value) {
            currentCargoByID[cargo.ID] = cargo;
        }
        const nextValue: CargoWithServices[] = [];
        const newCargos = []

        for (const cargo of cargos) {
            if (currentCargoByID[cargo.ID]) {
                nextValue.push(currentCargoByID[cargo.ID]);
            } else {
                newCargos.push(cargo);
            }
        }

        let promise = Promise.resolve();
        if (newCargos.length > 0) {
            promise = Promise.all(newCargos.map(cargo => getCargoNecessaryServices(cargo.ID))).then(results => {
                for (let i = 0; i < newCargos.length; ++i) {
                    nextValue.push({
                        ...newCargos[i],
                        ServiceList: results[i]
                    })
                }
            })
        }

        setCountAppending(newCargos.length);
        promise.then(() => {
            setCountAppending(0);
            onChange && onChange(nextValue);
        }, e => {
            setCountAppending(0);
            handleError(e);
        });
    }

    // закрываем диалог при обновлении страницы
    useEffect(() => {
        if (openSelectDialog) {
            setOpenSelectDialog(false);
        }
    }, [])

    const handleOpenSelectCargoDialogClick = () => {
        setOpenSelectDialog(true).then(() => null);
    }

    const handleSelectCargoDialogClose = () => {
        setOpenSelectDialog(false).then(() => null);
    }

    const handleRemoveCargoClick = (cargo: Pick<Cargo, "ID">) => () => {
        setCargoToConfirmRemove(cargo.ID);
    }

    const handleConfirmRemoveCargo = () => {
        changeCargoList(value.filter(x => x.ID != cargoToConfirmRemove));
        setCargoToConfirmRemove(null);
    }

    const handleCargoListChange: CargoSelectDialogProps["onChange"] = (cargos) => {
        setOpenSelectDialog(false).then(() => {
            changeCargoList(cargos.map(x => ({
                ID: x.code,
                Direction: {
                    "Завоз": "Import",
                    "Вывоз": "Export",
                    "Транзит": "Transit",
                }[x.direction] as Cargo["Direction"],
                Expeditor: "",
                Name: x.name,
                Quantity: x.quantity,
                StorageID: x.storageName,
                Type: x.type,
            })));
        });
    }

    const handlerOpenCargoDialogClick = (cargo: CargoWithServices) => () => {
        setEditCargoID(cargo.ID);
    }

    const handlerSelectServiceDialogClose = () => {
        delEditCargoID().then(() => null);
    }

    const handleCargoServicesChange = (cargo: Cargo) => (services: Service[]) => {
        const newValue: CargoWithServices[] = value?.map(c => {
            if (c.ID == cargo.ID) {
                return {...c, ServiceList: services};
            }
            return c;
        });
        delEditCargoID().then(() => {
            onChange && onChange(newValue)
        });
    }

    return (
        <Grid direction="column" container wrap="nowrap" spacing={2}>
            <ConfirmActionDialog
                open={cargoToConfirmRemove !== null}
                dialogId={"removeCargo"}
                dialogTitle="Вы хотите удалить груз?"
                confirmButtonText="Подтвердить"
                onClose={() => setCargoToConfirmRemove(null)}
                onConfirm={handleConfirmRemoveCargo}
            >
                <Typography width="100%" align="center" variant="screenText">Вы хотите удалить груз?</Typography>
            </ConfirmActionDialog>
            {value.map(cargo => (
                <Grid item key={cargo.ID}>
                    <AutoVisitCargoCard
                        cargo={cargo}
                        editable={true}
                        disabled={disabled}
                        expandServices={true}
                        onChangeServiceClick={handlerOpenCargoDialogClick(cargo)}
                        onRemoveClick={handleRemoveCargoClick(cargo)}
                    />
                </Grid>
            ))}
            <Grid item>
                <CardButton
                    startIcon={<Add/>}
                    endIcon={<ChevronRight/>}
                    onClick={handleOpenSelectCargoDialogClick}
                    disabled={disabled}
                    fullWidth
                    size="large"
                >
                    Добавить груз
                </CardButton>
            </Grid>

            <CargoSelectDialog
                value={value.map(x => ({
                    code: x.ID,
                    name: x.Name,
                    storageName: x.StorageID,
                    direction: {
                        "Import": "Завоз",
                        "Export": "Вывоз",
                        "Transit": "Транзит",
                    }[x.Direction] as CargoV2["direction"],
                    quantity: x.Quantity,
                    order: null,
                    weight: null,
                    type: x.Type,
                }))}
                onChange={handleCargoListChange}
                autoVisitId={autoVisitId}
                open={openSelectDialog}
                onClose={handleSelectCargoDialogClose}
            />

            <ServiceSelectDialog
                open={editCargo != null}
                onClose={handlerSelectServiceDialogClose}
                carrierId={carrierId}
                cargoId={editCargo?.ID}
                autoVisitId={autoVisitId}
                value={editCargo?.ServiceList}
                onChange={handleCargoServicesChange(editCargo)}
            />
        </Grid>
    );
}
